import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Lock, Search, ArrowUp, ShoppingCart, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Ebooks = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    
    try {
      if (token) {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);
      }

      // Enviar el token: el backend filtra el material por programa segun la usuaria
      const ebooksRes = await axios.get(`${BACKEND_URL}/api/content?category=ebook`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setEbooks(ebooksRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAccessContent = (content) => {
    const contentLevels = content.levels || [content.level];
    
    if (!user) return contentLevels.includes('free');
    // Material asignado a programas: el backend solo lo devuelve si la usuaria tiene alguno de esos programas
    if ((content.program_ids || []).length > 0) return true;
    
    const accessLevels = {
      'free': ['free'],
      'principiante': ['free', 'nivel_1'],
      'intermedio': ['free', 'nivel_1', 'nivel_2'],
      'avanzado': ['free', 'nivel_1', 'nivel_2', 'nivel_3'],
      'premium': ['free', 'nivel_1', 'nivel_2', 'nivel_3', 'premium']
    };

    const userAllowedLevels = accessLevels[user.subscription_level] || ['free'];
    return contentLevels.some(lvl => userAllowedLevels.includes(lvl));
  };

  const handleBuyContent = async (content) => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Debes iniciar sesión para comprar');
      navigate(`/login?redirect=/checkout?ebook=${content.content_id}`);
      return;
    }

    navigate(`/checkout?ebook=${content.content_id}`);
  };

  const filteredEbooks = ebooks.filter(ebook =>
    ebook.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ebook.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const levelNames = {
    'free': 'Gratis',
    'nivel_1': 'Nivel 1',
    'nivel_2': 'Nivel 2',
    'nivel_3': 'Nivel 3',
    'premium': 'Premium'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm font-medium uppercase tracking-wider">Biblioteca Digital</span>
          </div>
          
          <h1 className="font-barlow text-4xl md:text-6xl font-black uppercase text-white mb-4">
            E-BOOKS
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Biblioteca de recursos descargables para tu entrenamiento y nutrición
          </p>
          
          {user && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-pink-500/20 border border-pink-500/30 rounded-full">
              <span className="text-pink-400 text-sm font-medium">
                Tu nivel: {levelNames[user.subscription_level] || 'Gratuito'}
              </span>
            </div>
          )}
        </div>

        {/* Buscador */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar ebooks..."
              className="pl-12 rounded-full py-6 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Lista de Ebooks */}
        {filteredEbooks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl">
            <FileText className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No hay ebooks disponibles</h3>
            <p className="text-zinc-500">Pronto agregaremos nuevo contenido</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEbooks.map((ebook) => {
              const hasAccess = canAccessContent(ebook);
              const canBuyIndividually = ebook.price && ebook.price > 0;
              const ebookLevels = ebook.levels || [ebook.level];
              
              return (
                <div
                  key={ebook.content_id}
                  className="group bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden hover:border-red-500/50 transition-all"
                >
                  {/* Cover */}
                  <div className="aspect-[3/4] bg-zinc-800 relative flex items-center justify-center overflow-hidden">
                    {ebook.cover_image || ebook.thumbnail_url ? (
                      <img 
                        src={(ebook.cover_image || ebook.thumbnail_url).startsWith('http') ? (ebook.cover_image || ebook.thumbnail_url) : `${BACKEND_URL}${ebook.cover_image || ebook.thumbnail_url}`}
                        alt={ebook.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <FileText className="w-24 h-24 text-zinc-600" />
                    )}

                    {!hasAccess && (
                      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                        <Lock className="w-10 h-10 text-white mb-2" />
                        <span className="text-sm font-medium text-white">Bloqueado</span>
                      </div>
                    )}

                    {/* Badge de nivel */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                      {ebookLevels.slice(0, 2).map((lvl, idx) => (
                        <span key={idx} className="text-xs font-bold px-2 py-1 rounded-full bg-zinc-900/80 text-zinc-300 border border-zinc-700">
                          {levelNames[lvl] || lvl}
                        </span>
                      ))}
                    </div>

                    {/* Badge de precio */}
                    {!hasAccess && canBuyIndividually && (
                      <span className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full bg-green-500 text-white">
                        ${ebook.price}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-barlow font-bold text-white text-lg mb-2 line-clamp-2">
                      {ebook.title}
                    </h3>
                    <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                      {ebook.description || 'Ebook descargable en PDF'}
                    </p>

                    {hasAccess ? (
                      <a 
                        href={(() => {
                          if (ebook.video_source === 'drive' || ebook.external_url) {
                            let driveUrl = ebook.external_url || ebook.file_path;
                            if (driveUrl.includes('drive.google.com/file/d/')) {
                              const match = driveUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
                              if (match) {
                                return `https://drive.google.com/uc?export=download&id=${match[1]}`;
                              }
                            }
                            return driveUrl;
                          }
                          return ebook.file_path?.startsWith('http')
                            ? ebook.file_path
                            : `${BACKEND_URL}${ebook.file_path}`;
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-red-600 hover:bg-red-500 text-white rounded-full py-3 font-bold transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Descargar PDF
                      </a>
                    ) : (
                      <div className="space-y-2">
                        {canBuyIndividually && (
                          <Button
                            onClick={() => handleBuyContent(ebook)}
                            className="w-full bg-green-600 hover:bg-green-500 text-white rounded-full py-3 font-bold"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Comprar ${ebook.price}
                          </Button>
                        )}
                        <a href="/programs" className="block">
                          <Button
                            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-full py-3 font-bold"
                          >
                            <ArrowUp className="w-4 h-4 mr-2" />
                            {canBuyIndividually ? 'O Subir de Nivel' : 'Subir de Nivel'}
                          </Button>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {!user && (
          <div className="mt-16 bg-gradient-to-r from-red-600/20 to-pink-600/20 border border-red-500/30 rounded-3xl p-12 text-center">
            <h2 className="font-barlow text-3xl font-bold text-white uppercase mb-4">
              Accede a todos los ebooks
            </h2>
            <p className="text-zinc-400 mb-6">
              Inicia sesión o suscríbete para desbloquear todo el contenido
            </p>
            <a href="/programs">
              <Button className="bg-red-600 hover:bg-red-500 text-white rounded-full px-12 py-6 font-bold">
                Ver Programas
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ebooks;
